/**
 * Shared Workstation Layout Components
 *
 * Reusable institutional desktop-app style components used across all pages.
 * Pattern: ink menu bar (Design ID chrome), toolbar rows, fieldset sections, native HTML inputs,
 * dense DataGrid, and status bar.
 *
 * THEME MECHANISM (2026-07-14): the WS tokens are no longer light-only
 * constants. `WS` is an object whose properties are GETTERS that resolve the
 * active light/dark token set (derived from the MUI theme palettes in
 * theme/muiTheme.ts via getWorkstationTokens) by reading the Redux theme mode
 * at PROPERTY-ACCESS time. Because a theme toggle re-renders the whole tree
 * under ThemeProvider (main.tsx ThemedApp selects the mode), every render-time
 * `WS.xxx` access — including spreads of inputSx/readonlySx/selectSx/labelSx,
 * which are themselves getter-backed — picks up the new mode. The ~296
 * consumers keep the exact same `WS.xxx` property-access API.
 *
 * Caveats: (a) module-level snapshots (e.g. `const x = WS.BG_HEADER` at file
 * scope) freeze at import time — read WS inside render instead; (b) the ink
 * MenuBar/StatusBar/DlgTitle chrome (tokens.HEADER_BAR) is intentionally
 * static — ink chrome reads correctly in both modes (Design ID masthead).
 *
 * For theme-context-based derivation (preferred in new code):
 *   const ws = getWS(useTheme());
 */
import { Close, Minimize, CropSquare, FilterNone } from '@/components/ui/qicons';
import React, { useState, useRef, useCallback } from 'react';
import { Box, Button, Chip, Divider, IconButton, Paper, useTheme, type SxProps, type Theme } from '@mui/material';
import { NumericInput, type NumericInputProps } from './NumericInput';
import {
  lightTheme,
  darkTheme,
  getWorkstationTokens,
  WS_FONT,
  type WorkstationTokens,
} from '@/theme/muiTheme';
import { ACCENT, LIGHT, DARK, HEADER_BAR, FONTS } from '@/theme/tokens';
import { store } from '@/app/store';

/* ─── Theme-aware tokens ───────────────────────────────────────────────────── */
const LIGHT_WS = getWorkstationTokens(lightTheme);
const DARK_WS = getWorkstationTokens(darkTheme);

const activeWs = (): WorkstationTokens =>
  store.getState().theme.mode === 'dark' ? DARK_WS : LIGHT_WS;

/** Derive the workstation token set for an explicit theme (e.g. useTheme()). */
export const getWS = (theme: Theme): WorkstationTokens => getWorkstationTokens(theme);

export const WS = {
  get BORDER() { return activeWs().BORDER; },
  get BG_PANEL() { return activeWs().BG_PANEL; },
  get BG_INPUT() { return activeWs().BG_INPUT; },
  get BG_READONLY() { return activeWs().BG_READONLY; },
  get BG_HEADER() { return activeWs().BG_HEADER; },
  get BG_TOOLBAR() { return activeWs().BG_TOOLBAR; },
  get BG_GRID() { return activeWs().BG_GRID; },
  get BG_YELLOW() { return activeWs().BG_YELLOW; },
  get TEXT_LABEL() { return activeWs().TEXT_LABEL; },
  get TEXT_VALUE() { return activeWs().TEXT_VALUE; },
  get TEXT_READONLY() { return activeWs().TEXT_READONLY; },
  get TEXT_LEGEND() { return activeWs().TEXT_LEGEND; },
  get INPUT_BORDER() { return activeWs().INPUT_BORDER; },
  get ACCENT() { return activeWs().ACCENT; },
  get BTN_TEXT() { return activeWs().BTN_TEXT; },
  get BTN_HOVER() { return activeWs().BTN_HOVER; },
  get BTN_ACTIVE() { return activeWs().BTN_ACTIVE; },
  FONT: WS_FONT,
};

/* ─── Input Styles ─────────────────────────────────────────────────────────── */
// Getter-backed so that `{...inputSx}` / `style={inputSx}` at render time
// resolves the ACTIVE theme's tokens (a plain spread at module scope would
// freeze the light values — hence no `{...inputSx}` derivation below).
const baseInputStyle = (
  bg: () => string,
  fg: () => string,
  extra: React.CSSProperties = {},
): React.CSSProperties => ({
  height: 22, padding: '1px 4px', fontSize: '0.72rem',
  outline: 'none', boxSizing: 'border-box' as const, width: '100%',
  get fontFamily() { return WS.FONT; },
  get border() { return `1px solid ${WS.INPUT_BORDER}`; },
  get backgroundColor() { return bg(); },
  get color() { return fg(); },
  ...extra,
});

export const inputSx: React.CSSProperties = baseInputStyle(() => WS.BG_INPUT, () => WS.TEXT_VALUE);

export const readonlySx: React.CSSProperties = baseInputStyle(() => WS.BG_READONLY, () => WS.TEXT_READONLY);

export const selectSx: React.CSSProperties = baseInputStyle(() => WS.BG_INPUT, () => WS.TEXT_VALUE, { cursor: 'pointer' });

export const labelSx: React.CSSProperties = {
  fontSize: '0.68rem',
  whiteSpace: 'nowrap' as const, fontWeight: 500, lineHeight: '22px',
  paddingRight: 4, minWidth: 'fit-content',
  get fontFamily() { return WS.FONT; },
  get color() { return WS.TEXT_LABEL; },
};

/* ─── Field Component ──────────────────────────────────────────────────────── */
export function F({
  label, value, onChange, readOnly, type = 'text', options,
  labelWidth, yellow, rows, placeholder, required, error, width, multiline,
  numeric, allowShorthand, allowNegative, displayPrecision, minValue, maxValue, wholeNumberOnly, lotSize, allowDecimals,
}: {
  label: string; value?: string | number; onChange?: (v: string) => void;
  readOnly?: boolean; type?: string;
  options?: { value: string; label: string }[];
  labelWidth?: number; yellow?: boolean; rows?: number;
  placeholder?: string; required?: boolean; error?: string; width?: string | number;
  multiline?: boolean;
  // Numeric shorthand mode
  numeric?: boolean;
  allowShorthand?: boolean;
  allowNegative?: boolean;
  allowDecimals?: boolean;
  displayPrecision?: number;
  minValue?: number;
  maxValue?: number;
  wholeNumberOnly?: boolean;
  lotSize?: number;
}) {
  const lw = labelWidth || 90;

  // Numeric shorthand mode — delegate to NumericInput
  if (numeric) {
    return (
      <NumericInput
        label={label}
        value={value ?? 0}
        onChange={(n) => onChange?.(String(n))}
        allowShorthand={allowShorthand}
        allowNegative={allowNegative}
        allowDecimals={allowDecimals}
        displayPrecision={displayPrecision}
        minValue={minValue}
        maxValue={maxValue}
        wholeNumberOnly={wholeNumberOnly}
        lotSize={lotSize}
        placeholder={placeholder}
        labelWidth={labelWidth}
        readOnly={readOnly}
        error={error}
        required={required}
        yellow={yellow}
        width={width}
      />
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: width || '100%' }}>
      <label
        title={label}
        style={{ ...labelSx, minWidth: lw, maxWidth: lw, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {label}{required && <span style={{ color: 'var(--q-down)' }}>*</span>}
      </label>
      {options ? (
        <div style={{ flex: 1 }}>
          <select
            style={{ ...selectSx, borderColor: error ? 'var(--q-down)' : WS.INPUT_BORDER }}
            value={value ?? ''} onChange={(e) => onChange?.(e.target.value)} disabled={readOnly}
          >
            {(!value || value === '') && <option value="">— Select —</option>}
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {error && <div style={{ fontSize: '0.6rem', color: 'var(--q-down)', marginTop: 1 }}>{error}</div>}
        </div>
      ) : (multiline || (rows && rows > 1)) ? (
        <div style={{ flex: 1 }}>
          <textarea
            style={{ ...inputSx, height: rows * 18, resize: 'none',
              borderColor: error ? 'var(--q-down)' : WS.INPUT_BORDER,
              backgroundColor: yellow ? WS.BG_YELLOW : readOnly ? WS.BG_READONLY : WS.BG_INPUT }}
            value={value ?? ''} onChange={(e) => onChange?.(e.target.value)}
            readOnly={readOnly} placeholder={placeholder}
          />
          {error && <div style={{ fontSize: '0.6rem', color: 'var(--q-down)', marginTop: 1 }}>{error}</div>}
        </div>
      ) : (
        <div style={{ flex: 1 }}>
          <input
            style={{ ...inputSx, borderColor: error ? 'var(--q-down)' : WS.INPUT_BORDER,
              backgroundColor: yellow ? WS.BG_YELLOW : readOnly ? WS.BG_READONLY : WS.BG_INPUT }}
            type={type} value={value ?? ''} onChange={(e) => onChange?.(e.target.value)}
            readOnly={readOnly} placeholder={placeholder}
          />
          {error && <div style={{ fontSize: '0.6rem', color: 'var(--q-down)', marginTop: 1 }}>{error}</div>}
        </div>
      )}
    </div>
  );
}

/* ─── Fieldset Section ─────────────────────────────────────────────────────── */
export function Section({ title, children, cols = 2, sx }: { title: string; children: React.ReactNode; cols?: number; sx?: SxProps<Theme> }) {
  return (
    <Box component="fieldset" sx={{ border: WS.BORDER, borderRadius: 0, margin: '4px 4px', padding: '4px 6px 6px', ...sx as any }}>
      <legend style={{ fontSize: '0.74rem', fontWeight: 600, color: WS.TEXT_LEGEND, fontFamily: FONTS.serif, letterSpacing: '0.01em', padding: '0 4px' }}>
        {title}
      </legend>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '3px 12px' }}>
        {children}
      </div>
    </Box>
  );
}

/* ─── Toolbar Button ───────────────────────────────────────────────────────── */
export function TBtn({ label, icon, onClick, disabled, primary, active, children }: {
  label?: string; icon?: React.ReactNode; onClick?: () => void;
  disabled?: boolean; primary?: boolean; active?: boolean; children?: React.ReactNode;
}) {
  return (
    <Button size="small" variant={primary ? 'contained' : 'text'} startIcon={icon}
      disabled={disabled} onClick={onClick}
      sx={{
        fontSize: '0.63rem', textTransform: 'none', py: 0, px: 0.7,
        minHeight: 22, minWidth: 0, borderRadius: 0,
        color: primary ? '#fff' : WS.BTN_TEXT,
        bgcolor: primary ? WS.ACCENT : active ? WS.BTN_ACTIVE : 'transparent',
        '&:hover': { bgcolor: primary ? ACCENT.deeper : WS.BTN_HOVER },
        lineHeight: 1.2,
      }}
    >
      {label}{children}
    </Button>
  );
}

/* ─── Page Shell ───────────────────────────────────────────────────────────── */
/**
 * PageShell — THE page frame. Every route in the portal renders inside one of these.
 *
 * Phase 8 W2 extended it with an optional masthead. Before that, the portal had three
 * visual registers: `pages/**` mounted this shell (277 files) and got the ink MenuBar
 * + serif wordmark; `admin/**` (752 files) and `modules/**` mounted NOTHING and landed
 * as bare MUI on the page background. Navigating between them crossed three designs.
 *
 * The extension is deliberately additive: children-only usage is byte-identical to the
 * old behaviour, so the 277 existing consumers keep their own MenuBar and are untouched.
 * Pass `title` and the shell draws the masthead for you — that is the path admin and the
 * workbenches take, and the reason the whole admin plane converged in one edit.
 */
export interface PageShellProps {
  children: React.ReactNode;
  /** Draws the ink masthead with the serif wordmark. Omit to supply your own MenuBar. */
  title?: string;
  /** Secondary line in the masthead — context, not decoration. */
  subtitle?: string;
  /** Rendered into the masthead's right side (actions, counts, status). */
  toolbar?: React.ReactNode;
}

export const PageShell = React.forwardRef<HTMLDivElement, PageShellProps>(
  function PageShell({ children, title, subtitle, toolbar }, ref) {
    return (
      <Box ref={ref} sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', bgcolor: WS.BG_PANEL, fontFamily: WS.FONT }}>
        {title && (
          <MenuBar title={title}>
            {subtitle && (
              <>
                <MenuSep />
                <MenuInfo>{subtitle}</MenuInfo>
              </>
            )}
            {toolbar && <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>{toolbar}</Box>}
          </MenuBar>
        )}
        {children}
      </Box>
    );
  }
);

/* ─── Menu Bar ─────────────────────────────────────────────────────────────── */
export function MenuBar({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', bgcolor: HEADER_BAR.bg, color: HEADER_BAR.fg,
      height: 24, px: 1, fontSize: '0.72rem', fontWeight: 600, gap: 2,
      borderBottom: `2px solid ${ACCENT.core}`,
    }}>
      <span style={{ fontFamily: FONTS.serif, fontSize: '0.86rem', letterSpacing: '0.015em' }}>{title}</span>
      {children}
    </Box>
  );
}

export function MenuSep() {
  return <span style={{ color: HEADER_BAR.fgSoft }}>|</span>;
}

export function MenuInfo({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize: '0.68rem', color: HEADER_BAR.fgSoft }}>{children}</span>;
}

/* ─── Toolbar Row ──────────────────────────────────────────────────────────── */
export function Toolbar({ children }: { children?: React.ReactNode }) {
  return (
    <Box sx={{
      display: 'flex', gap: 0.5, px: 0.5, py: '2px', bgcolor: WS.BG_TOOLBAR,
      borderBottom: WS.BORDER, flexWrap: 'wrap', alignItems: 'center',
    }}>
      {children}
    </Box>
  );
}

export function ToolbarSep() {
  return <Divider orientation="vertical" flexItem sx={{ mx: 0.3, borderColor: WS.INPUT_BORDER }} />;
}

/* ─── Status Bar ───────────────────────────────────────────────────────────── */
export function StatusBar({ children }: { children?: React.ReactNode }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 2, px: 1, py: '2px',
      bgcolor: HEADER_BAR.bg, color: HEADER_BAR.fgSoft, fontSize: '0.62rem',
      borderTop: `1px solid ${ACCENT.line}`, fontFamily: WS.FONT,
    }}>
      {children}
    </Box>
  );
}

/* ─── Stat Chip ────────────────────────────────────────────────────────────── */
export function StatChip({ label, color = 'blue' }: { label: string; color?: 'blue' | 'green' | 'orange' | 'red' }) {
  const dark = useTheme().palette.mode === 'dark';
  // Identity semantics: blue = accent/informational, green = up, orange = warn, red = down.
  const colors = dark ? {
    blue: { bg: ACCENT.tint, fg: ACCENT.core },
    green: { bg: 'rgba(127, 191, 122, 0.18)', fg: DARK.up },
    orange: { bg: 'rgba(203, 168, 103, 0.18)', fg: DARK.warn },
    red: { bg: 'rgba(217, 141, 141, 0.18)', fg: DARK.down },
  } : {
    blue: { bg: ACCENT.tint, fg: ACCENT.deep },
    green: { bg: 'rgba(62, 122, 62, 0.12)', fg: LIGHT.up },
    orange: { bg: 'rgba(138, 106, 51, 0.12)', fg: LIGHT.warn },
    red: { bg: 'rgba(158, 58, 58, 0.12)', fg: LIGHT.down },
  };
  const c = colors[color];
  return (
    <Chip label={label} size="small"
      sx={{ borderRadius: 0, fontSize: '0.62rem', fontWeight: 700, height: 18, bgcolor: c.bg, color: c.fg }}
    />
  );
}

/* ─── DataGrid Container ──────────────────────────────────────────────────── */
export function GridContainer({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ flex: 1, mx: 0.5, mb: 0.5, border: WS.BORDER, bgcolor: WS.BG_GRID, overflow: 'hidden' }}>
      {children}
    </Box>
  );
}

/* ─── Dialog Title (dark blue, draggable via CSS transform, maximize/restore) ── */
export function DlgTitle({ children, onClose }: { children: React.ReactNode; onClose?: () => void }) {
  const titleRef = useRef<HTMLDivElement>(null);
  const [maximized, setMaximized] = useState(false);
  const dragState = useRef({ active: false, startX: 0, startY: 0, dx: 0, dy: 0 });

  const getPaper = (): HTMLElement | null => {
    let el = titleRef.current?.parentElement;
    while (el && !el.classList.contains('MuiPaper-root')) el = el.parentElement;
    return el;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (maximized || (e.target as HTMLElement).closest('button')) return;
    const paper = getPaper();
    if (!paper) return;
    dragState.current.active = true;
    dragState.current.startX = e.clientX - dragState.current.dx;
    dragState.current.startY = e.clientY - dragState.current.dy;

    const onMove = (ev: MouseEvent) => {
      if (!dragState.current.active) return;
      dragState.current.dx = ev.clientX - dragState.current.startX;
      dragState.current.dy = ev.clientY - dragState.current.startY;
      paper.style.transform = `translate(${dragState.current.dx}px, ${dragState.current.dy}px)`;
    };
    const onUp = () => {
      dragState.current.active = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const toggleMaximize = () => {
    const paper = getPaper();
    if (!paper) return;
    const next = !maximized;
    if (next) {
      // Save current position, go fullscreen
      paper.style.transform = 'none';
      paper.style.width = '100vw';
      paper.style.height = '100vh';
      paper.style.maxWidth = '100vw';
      paper.style.maxHeight = '100vh';
      paper.style.margin = '0';
      // Also fix the Dialog scroll container
      const container = paper.parentElement;
      if (container) { container.style.alignItems = 'flex-start'; container.style.justifyContent = 'flex-start'; }
    } else {
      // Restore
      paper.style.transform = `translate(${dragState.current.dx}px, ${dragState.current.dy}px)`;
      paper.style.width = '';
      paper.style.height = '';
      paper.style.maxWidth = '';
      paper.style.maxHeight = '';
      paper.style.margin = '';
      const container = paper.parentElement;
      if (container) { container.style.alignItems = ''; container.style.justifyContent = ''; }
    }
    setMaximized(next);
  };

  const [minimized, setMinimized] = useState(false);

  // Auto-detect close: if no onClose prop, try clicking the MUI Dialog backdrop
  const handleClose = onClose || (() => {
    const paper = getPaper();
    if (!paper) return;
    // Find the MUI Dialog backdrop (sibling of the scroll container)
    const dialogRoot = paper.closest('.MuiDialog-root');
    const backdrop = dialogRoot?.querySelector('.MuiBackdrop-root') as HTMLElement;
    if (backdrop) backdrop.click();
  });

  const handleMinimize = () => {
    const paper = getPaper();
    if (!paper) return;
    setMinimized(!minimized);
    const content = paper.querySelector('.MuiDialogContent-root, [class*="DialogContent"]') as HTMLElement;
    const actions = paper.querySelector('.MuiDialogActions-root, [class*="DialogActions"]') as HTMLElement;
    if (content) content.style.display = minimized ? '' : 'none';
    if (actions) actions.style.display = minimized ? '' : 'none';
    // Also collapse any non-title children
    Array.from(paper.children).forEach((child, i) => {
      if (i === 0) return; // skip title bar
      (child as HTMLElement).style.display = minimized ? '' : 'none';
    });
  };

  return (
    <Box
      ref={titleRef}
      onMouseDown={handleMouseDown}
      onDoubleClick={toggleMaximize}
      sx={{
        borderBottom: WS.BORDER, py: 0.5, px: 1, bgcolor: HEADER_BAR.bg, color: HEADER_BAR.fg,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        cursor: 'move', userSelect: 'none',
      }}
    >
      <span style={{ fontSize: '0.75rem', fontWeight: 600, fontFamily: WS.FONT, flex: 1 }}>{children}</span>
      <Box sx={{ display: 'flex', gap: '1px', ml: 1 }}>
        <IconButton size="small" onClick={handleMinimize} sx={{ color: '#fff', p: '2px' }} title={minimized ? 'Restore' : 'Minimize'}>
          <Minimize sx={{ fontSize: 14 }} />
        </IconButton>
        <IconButton size="small" onClick={toggleMaximize} sx={{ color: '#fff', p: '2px' }} title={maximized ? 'Restore' : 'Maximize'}>
          {maximized ? <FilterNone sx={{ fontSize: 13 }} /> : <CropSquare sx={{ fontSize: 14 }} />}
        </IconButton>
        <IconButton size="small" onClick={handleClose} sx={{ color: '#fff', p: '2px', '&:hover': { bgcolor: 'var(--q-down)' } }} title="Close">
          <Close sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
}
